import { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";

export type CoachMark = { selector: string; title: string; text: string };

/**
 * Lightweight guided tour: dims the page, rings the target element and shows a balloon next to it.
 * Marks whose selector is not on screen are skipped, so a tour can span wizard steps.
 * Stored per key in localStorage; `?tour=1` forces it.
 */
export function CoachMarks({ storageKey, marks, active, onDone }: { storageKey: string; marks: CoachMark[]; active: boolean; onDone?: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active) return;
    const forced = new URLSearchParams(window.location.search).get("tour") === "1";
    if (forced || !localStorage.getItem(storageKey)) { setOpen(true); setI(0); }
  }, [active, storageKey]);

  const visible = marks.filter((m) => document.querySelector(m.selector));
  const mark = visible[i];

  useLayoutEffect(() => {
    if (!open || !mark) return;
    const el = document.querySelector<HTMLElement>(mark.selector);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const update = () => setRect(el.getBoundingClientRect());
    const id = window.setTimeout(update, 350);
    update();
    window.addEventListener("resize", update); window.addEventListener("scroll", update, true);
    return () => { window.clearTimeout(id); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [open, mark?.selector, i]);

  if (!open || !mark || !rect) return null;
  const finish = () => { localStorage.setItem(storageKey, new Date().toISOString()); setOpen(false); onDone?.(); };
  const next = () => { if (i + 1 >= visible.length) finish(); else setI(i + 1); };
  const below = rect.bottom + 180 < window.innerHeight;
  const top = below ? rect.bottom + 12 : Math.max(12, rect.top - 12 - 150);
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - 332);

  return (
    <div className="fixed inset-0 z-[100]" aria-live="polite">
      <div className="absolute inset-0 bg-black/55" onClick={finish} />
      <div className="pointer-events-none absolute rounded-lg ring-4 ring-primary/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
        style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, background: "transparent" }} />
      <div className="absolute w-[320px] rounded-xl border bg-background p-4 shadow-2xl" style={{ top, left }} role="dialog">
        <div className="text-xs text-muted-foreground">{i + 1} / {visible.length}</div>
        <div className="mt-1 font-semibold">{mark.title}</div>
        <p className="mt-1 text-sm text-muted-foreground">{mark.text}</p>
        <div className="mt-3 flex justify-between">
          <Button variant="ghost" size="sm" onClick={finish}>{t("tour.skip")}</Button>
          <Button size="sm" onClick={next}>{i + 1 >= visible.length ? t("tour.done") : t("tour.next")}</Button>
        </div>
      </div>
    </div>
  );
}
